/**
 * The text layout of a flyer, apart from any canvas so it can be tested: lines are broken at
 * spaces and shrunk until they fit their box.
 */

/** The width of `text` in pixels at a font size and weight. */
export type Measure = (text: string, size: number, weight: number) => number;

export interface FitOptions {
  maxWidth: number;
  maxLines: number;
  maxSize: number;
  minSize: number;
  weight: number;
}

export interface FittedText {
  size: number;
  lines: string[];
}

/** Breaks `text` into lines no wider than `maxWidth`, at spaces; a longer word keeps a line. */
export function wrapText(
  text: string,
  maxWidth: number,
  size: number,
  weight: number,
  measure: Measure,
): string[] {
  const words = text.split(/\s+/).filter((word) => word.length > 0);
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const candidate = line === '' ? word : `${line} ${word}`;
    if (line !== '' && measure(candidate, size, weight) > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  }
  if (line !== '') lines.push(line);
  return lines;
}

const ELLIPSIS = '…';

/** Cuts `line` at the end until it fits, with an ellipsis. */
function shorten(line: string, maxWidth: number, size: number, weight: number, measure: Measure) {
  let text = line;
  while (text.length > 1 && measure(`${text}${ELLIPSIS}`, size, weight) > maxWidth) {
    text = text.slice(0, -1).trimEnd();
  }
  return `${text}${ELLIPSIS}`;
}

/**
 * The largest size, from `maxSize` down to `minSize`, at which `text` fits in `maxLines` lines of
 * `maxWidth`. Text that does not fit even at the smallest size is cut, with an ellipsis.
 */
export function fitText(text: string, options: FitOptions, measure: Measure): FittedText {
  const { maxWidth, maxLines, maxSize, minSize, weight } = options;
  for (let size = maxSize; size >= minSize; size -= 2) {
    const lines = wrapText(text, maxWidth, size, weight, measure);
    if (
      lines.length <= maxLines &&
      lines.every((line) => measure(line, size, weight) <= maxWidth)
    ) {
      return { size, lines };
    }
  }
  const lines = wrapText(text, maxWidth, minSize, weight, measure);
  const kept = lines.slice(0, maxLines);
  const last = kept.length - 1;
  const cut = lines.length > maxLines;
  return {
    size: minSize,
    lines: kept.map((line, index) =>
      (index === last && cut) || measure(line, minSize, weight) > maxWidth
        ? shorten(line, maxWidth, minSize, weight, measure)
        : line,
    ),
  };
}

/** Black or white, whichever reads better on `hex` (#rrggbb). */
export function textColorOn(hex: string): string {
  const value = Number.parseInt(hex.slice(1), 16);
  const channel = (shift: number) => {
    const c = ((value >> shift) & 0xff) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const luminance = 0.2126 * channel(16) + 0.7152 * channel(8) + 0.0722 * channel(0);
  return luminance > 0.45 ? '#111827' : '#ffffff';
}

/** A price in shekels as a flyer shows it: no agorot when there are none. */
export function flyerPrice(amountMinor: number): string {
  const whole = amountMinor % 100 === 0;
  const amount = new Intl.NumberFormat('he-IL', {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  }).format(amountMinor / 100);
  return `₪${amount}`;
}

/** The store's address as a flyer shows it, without its scheme or a closing slash. */
export function flyerAddress(website: string): string {
  return website
    .replace(/^https?:\/\//, '')
    .replace(/\/$/, '')
    .slice(0, 40);
}

/** A real sale: an old price above the price. Anything else shows the price alone. */
export function flyerSaving(price: {
  amountMinor: number;
  compareAtMinor: number | null;
}): number | null {
  const { amountMinor, compareAtMinor } = price;
  return compareAtMinor !== null && compareAtMinor > amountMinor
    ? compareAtMinor - amountMinor
    : null;
}

/** A file name for the downloaded image: the product's title, without characters files refuse. */
export function flyerFileName(title: string): string {
  const name = title
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .replace(/\p{Cc}+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80);
  return `${name === '' ? 'flyer' : name} - פלייר.png`;
}

/**
 * The title in two parts, as every template sets it: the first in the heavy type, the rest
 * beside it in another weight or colour. The break falls where the two parts are closest in
 * length, the first not the longer one; a title of one or two words stays whole.
 */
export function splitTitle(title: string): [string, string] {
  const words = title.split(/\s+/).filter((word) => word !== '');
  if (words.length <= 2) return [words.join(' '), ''];
  let best = 1;
  let bestGap = Number.POSITIVE_INFINITY;
  for (let at = 1; at < words.length; at += 1) {
    const first = words.slice(0, at).join(' ').length;
    const rest = words.slice(at).join(' ').length;
    const gap = Math.abs(first - rest) + (first > rest ? 1 : 0);
    if (gap < bestGap) {
      best = at;
      bestGap = gap;
    }
  }
  return [words.slice(0, best).join(' '), words.slice(best).join(' ')];
}

const sentencesOf = (text: string) =>
  text
    .split(/(?<=[.!?])\s+/)
    .map((sentence) => sentence.trim())
    .filter((sentence) => sentence !== '');

/**
 * The description's opening sentences, as many as fit in `maxChars`; the first one even when it
 * is longer.
 */
export function leadText(description: string, maxChars: number): string {
  const sentences = sentencesOf(description);
  let text = '';
  for (const sentence of sentences) {
    const next = text === '' ? sentence : `${text} ${sentence}`;
    if (text !== '' && next.length > maxChars) break;
    text = next;
  }
  return text;
}

/**
 * The description's opening sentences in its box: the most whole sentences that fit in `maxLines`
 * lines at a size between `maxSize` and `minSize`, never a sentence cut with an ellipsis. Null
 * when not even the first sentence fits: the template then leaves the description out.
 */
export function fitLead(
  description: string,
  options: FitOptions,
  measure: Measure,
): FittedText | null {
  const sentences = sentencesOf(description);
  for (let count = sentences.length; count > 0; count -= 1) {
    const text = sentences.slice(0, count).join(' ');
    for (let size = options.maxSize; size >= options.minSize; size -= 2) {
      const lines = wrapText(text, options.maxWidth, size, options.weight, measure);
      if (
        lines.length <= options.maxLines &&
        lines.every((line) => measure(line, size, options.weight) <= options.maxWidth)
      ) {
        return { size, lines };
      }
    }
  }
  return null;
}

/** A spec row a template can set as a large figure: a number, and a short unit after it. */
export interface FlyerFigure {
  /** The number (or numbers, `50×50`), with Latin digits. */
  value: string;
  /** The unit after it; empty without one. */
  unit: string;
  /** The row's label: what the figure is. */
  label: string;
}

const FIGURE = /^(\d[\d.,]*(?:\s*[x×X*]\s*\d[\d.,]*)*)\s*([^\d\s][^\d]{0,9})?$/;

/** The spec rows that read as a figure and a unit (`450°C`, `6 kW`, `50×50 ס"מ`), in order. */
export function flyerFigures(specs: readonly { label: string; value: string }[]): FlyerFigure[] {
  const figures: FlyerFigure[] = [];
  for (const row of specs) {
    const match = FIGURE.exec(row.value.trim());
    if (match === null) continue;
    const value = (match[1] ?? '').replace(/\s*[x×X*]\s*/g, '×');
    // A longer figure (`2×50×50`) is not one a glance takes in.
    if (value.length > 6) continue;
    figures.push({ value, unit: (match[2] ?? '').trim(), label: row.label.trim() });
  }
  return figures;
}

/**
 * The maker's line, as the store's page shows the maker (adr/0030): the manufacturer and the
 * model, `·` between them; empty when the page shows neither.
 */
export function makerLine(maker: { manufacturer: string | null; model: string | null }): string {
  return [maker.manufacturer, maker.model]
    .filter((part) => part !== null && part !== '')
    .join(' · ');
}

/** The month and year, in Hebrew, as a dated sheet prints them. */
export function flyerMonth(now: Date): string {
  return new Intl.DateTimeFormat('he-IL', { month: 'long', year: 'numeric' }).format(now);
}
